/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useOwnerProps } from '@elliemae/ds-props-helpers';
import { SimpleTruncatedTooltipText } from '@elliemae/ds-truncated-tooltip-text';
import React, { useCallback, useContext, useMemo } from 'react';
import ComboBoxContext from '../../ComboBoxCTX.js';
import { ComboboxDataTestid } from '../../ComboboxDataTestids.js';
import { ControlsInput } from '../controls-input/ControlsInput.js';
import { DropdownIndicator } from '../DropdownIndicator.js';
import { MultiSelectedValuesContainer } from '../multi-selected-values-container/index.js';
import {
  StyledControlsWrapper,
  StyledHeaderActionsWrapper,
  StyledSelection,
  StyledSingleSelectWraper,
  StyleHeaderActionsSeparator,
} from './styled.js';
import { useOnPillsNavigation } from './useOnPillsNavigation.js';

export const Controls = (): JSX.Element => {
  const {
    props,
    props: { inline, disabled, hasError, selectedValues, applyAriaDisabled, readOnly, isMenuOpenableWithReadOnly },
    selectedOptionsRef,
    setMenuState,
    setFocusOptionIdx,
    hasFocus,
    inputValue,
    listRef,
    focusOptionIdx,
    menuState,
    controlsWrapperRef,
    internalRef,
    shouldPreventContextMenuOpening,
  } = useContext(ComboBoxContext);

  const { getOwnerProps, getOwnerPropsArguments } = useOwnerProps(props);

  const handleOnClick = useCallback(() => {
    if (disabled) return;
    if (applyAriaDisabled || shouldPreventContextMenuOpening) {
      internalRef.current?.focus();
      return;
    }
    if (hasFocus && menuState && !inline) {
      setMenuState(false, 'click');
      setFocusOptionIdx('');
      internalRef.current?.blur();
      return;
    }
    internalRef.current?.focus();
    setMenuState(true, 'click');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabled,
    listRef,
    hasFocus,
    menuState,
    inline,
    setMenuState,
    focusOptionIdx,
    readOnly,
    applyAriaDisabled,
    isMenuOpenableWithReadOnly,
  ]);

  // this callback prevent to toggle the menu when clicking or removing pills
  const handleOnPillsClick: React.MouseEventHandler = useCallback(
    (e) => {
      if (applyAriaDisabled) return;
      if (menuState || disabled || shouldPreventContextMenuOpening) {
        e.stopPropagation();
      } else {
        internalRef.current?.focus();
        setMenuState(true, 'pill-click');
      }
    },
    [applyAriaDisabled, menuState, disabled, internalRef, setMenuState, shouldPreventContextMenuOpening],
  );

  // callback to prevent onBlur on the input when clicking in all the wrapper
  const handleOnMouseDown: React.MouseEventHandler = useCallback(
    (e) => {
      if (document.activeElement === internalRef.current || inline) {
        e.preventDefault();
      }
    },
    [internalRef, inline],
  );

  const { onKeyDownPills } = useOnPillsNavigation();

  const cols = useMemo(
    () => (!inline ? ['minmax(0px,max-content)', 'minmax(20px, auto)', '8px', '2.231rem'] : ['minmax(0px, auto)']),
    [inline],
  );
  return (
    <StyledControlsWrapper
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
      innerRef={controlsWrapperRef}
      readOnly={readOnly}
      cols={cols}
      disabled={disabled}
      applyAriaDisabled={applyAriaDisabled}
      hasError={hasError}
      inline={inline}
      onClick={handleOnClick}
      onMouseDown={handleOnMouseDown}
      onKeyDown={onKeyDownPills}
      aria-disabled={applyAriaDisabled}
      data-testid={ComboboxDataTestid.CONTROLS_WRAPPER}
    >
      {!inline && (
        <StyledSelection innerRef={selectedOptionsRef} onClick={handleOnPillsClick}>
          {Array.isArray(selectedValues) ? (
            <MultiSelectedValuesContainer />
          ) : (
            <StyledSingleSelectWraper
              readOnly={readOnly}
              data-testid={ComboboxDataTestid.SELECTED_VALUES}
              disabled={disabled}
              applyAriaDisabled={applyAriaDisabled}
            >
              {selectedValues && inputValue === '' ? <SimpleTruncatedTooltipText value={selectedValues.label} /> : null}
            </StyledSingleSelectWraper>
          )}
        </StyledSelection>
      )}

      <ControlsInput />
      {/** This empty div is a gap between the input and the drop down indicator  */}
      <div />
      {!inline && (
        <StyledHeaderActionsWrapper
          justifyItems="center"
          cols={['min-content', 'minmax(28px,max-content)']}
          justifyContent="flex-end"
        >
          <StyleHeaderActionsSeparator disabled={disabled} applyAriaDisabled={applyAriaDisabled} readOnly={readOnly} />
          <DropdownIndicator />
        </StyledHeaderActionsWrapper>
      )}
    </StyledControlsWrapper>
  );
};
